import React, { useRef, useState } from 'react';
import config from '../../configLoader';
import { EmployeeSickClaim } from '../../interfaces/EmployeeInterface';
import {
  PayrollEntryResult, PayrollSickClaimBody,
} from '../../interfaces/TimesheetEditInterface';
import { formatMinutesAs } from '../../util/hoursFormat';
import { money, parseAmt, parseDur, ymdLabel } from './clockParse';
import { EntryErrorBanner, EntryModalShell, performEntrySave } from './EntryModalShell';
import { DurEchoParts } from './echoParts';

/**
 * Sick claim editor (M2-B, design shot 07). Hours as a 10-key duration field
 * plus the payroll-entered hourly rate (WCDP shows it —
 * `hideSickClaimHourlyRate: false`); the claimed amount is DERIVED rate ×
 * hours and echoed live. `hourlyAmount` is omitted from the PUT when the rate
 * field is hidden, leaving the stored rate untouched (§8.6).
 */

export interface SickCarry { rateRaw: string; }

export interface SickClaimModalProps {
  employeeID: number;
  payPeriodID: number;
  ymd: string;
  employeeName: string;
  editSessionID: string;
  entry: EmployeeSickClaim | null;
  carry: SickCarry | null;
  onSaved: (result: PayrollEntryResult, close: boolean, carry: SickCarry) => void;
  onCancel: () => void;
  onConflict: (message: string) => void;
}

const SickClaimModal: React.FC<SickClaimModalProps> = ({
  employeeID, payPeriodID, ymd, employeeName, editSessionID,
  entry, carry, onSaved, onCancel, onConflict,
}) => {
  const isEdit = entry != null;
  const showRate = config.hideSickClaimHourlyRate !== true;
  const [minsRaw, setMinsRaw] = useState<string>(
    isEdit ? formatMinutesAs(entry!.minutesClaimed ?? 0, 'hhmm') : ''
  );
  const [rateRaw, setRateRaw] = useState<string>(
    isEdit
      ? (entry!.hourlyAmount ? String(entry!.hourlyAmount) : '')
      : carry?.rateRaw ?? ''
  );
  const [bad, setBad] = useState<'mins' | 'rate' | null>(null);
  const [banner, setBanner] = useState<{ title: string; body?: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const minsRef = useRef<HTMLInputElement>(null);
  const rateRef = useRef<HTMLInputElement>(null);

  const handleSave = async (close: boolean) => {
    const mins = parseDur(minsRaw);
    if (mins == null || mins <= 0) { setBad('mins'); minsRef.current?.focus(); return; }
    let rate = 0;
    if (showRate) {
      if (rateRaw.trim() === '') rate = 0;
      else {
        const r = parseAmt(rateRaw);
        if (r == null || r < 0) { setBad('rate'); rateRef.current?.focus(); return; }
        rate = r;
      }
    } else {
      rate = entry?.hourlyAmount ?? 0;
    }
    const amount = Math.round(rate * (mins / 60) * 100) / 100;

    const body: PayrollSickClaimBody = {
      ...(isEdit ? {} : { employeeID, payPeriodID, entryDate: ymd }),
      minutesClaimed: mins,
      amountClaimed: amount,
      // Omitted when the tenant hides the rate — the stored HourlyAmount stays
      // untouched on update (§8.6: the SET clause skips the column).
      ...(showRate ? { hourlyAmount: rate } : {}),
      description: isEdit ? entry!.description ?? 'Sick' : 'Sick',
      editSessionID,
    };

    setSaving(true);
    setBanner(null);
    const outcome = await performEntrySave('sickClaim', isEdit ? entry!.employeeSickClaimID : null, body);
    setSaving(false);
    if (!outcome.ok) {
      if (outcome.conflict) onConflict(outcome.message);
      else setBanner({ title: 'Could not save the sick claim.', body: outcome.message });
      return;
    }
    onSaved(outcome.result, close, { rateRaw: showRate ? rateRaw : '' });
  };

  const mins = parseDur(minsRaw);
  const rate = showRate ? parseAmt(rateRaw) ?? 0 : entry?.hourlyAmount ?? 0;
  const derived = mins != null && mins > 0 && rate > 0
    ? Math.round(rate * (mins / 60) * 100) / 100
    : null;

  return (
    <EntryModalShell
      kind="sickClaim"
      mode={isEdit ? 'edit' : 'add'}
      subtitle={`${ymdLabel(ymd)} · ${employeeName}`}
      banner={banner ? <EntryErrorBanner title={banner.title} body={banner.body} /> : null}
      echo={(
        <>
          <DurEchoParts raw={minsRaw} />
          {derived != null && <span>amount <b>{money(derived)}</b></span>}
        </>
      )}
      saving={saving}
      onSave={handleSave}
      onCancel={onCancel}
    >
      <div className="row2">
        <div className="field">
          <label>Hours claimed</label>
          <input
            ref={minsRef}
            className={`pe-in${bad === 'mins' ? ' bad' : ''}`}
            value={minsRaw}
            onChange={(ev) => { setMinsRaw(ev.target.value); if (bad === 'mins') setBad(null); }}
            placeholder="8 or 8:00"
            autoFocus
            autoComplete="off"
            spellCheck={false}
          />
        </div>
        {showRate && (
          <div className="field">
            <label>Hourly rate</label>
            <input
              ref={rateRef}
              className={`pe-in${bad === 'rate' ? ' bad' : ''}`}
              value={rateRaw}
              onChange={(ev) => { setRateRaw(ev.target.value); if (bad === 'rate') setBad(null); }}
              placeholder="22.50"
              autoComplete="off"
              spellCheck={false}
            />
          </div>
        )}
      </div>
      {showRate && (
        <p className="pe-note">
          The tenant shows the sick rate, so payroll enters it here — the claimed amount is derived
          (rate × hours). Approval still enforces a nonzero rate.
        </p>
      )}
    </EntryModalShell>
  );
};

export default SickClaimModal;
